import { signedDecimalUnits } from '@/lib/polymarketUs/ticketModel';
import { usPositionEconomics } from '@/lib/polymarketUs/positionEconomics';
import type { UsPositionMarks } from '@/hooks/useUsPositionMarks';
import type { TraderPositions } from '@/views/TraderDetail/usManagerReads';
import type { OrgUsPosition, UsOrgLoaded } from './useUsOrgRows';
import type { OwnerFilterKey } from '@/lib/ownerAttribution';

/** Display conversion only: source ledger decimal strings remain unchanged. */
function cents(units: bigint): number | null {
  const negative = units < 0n;
  const absolute = negative ? -units : units;
  const rounded = (absolute + 5_000_000_000n) / 10_000_000_000n;
  return rounded > BigInt(Number.MAX_SAFE_INTEGER) ? null : Number(negative ? -rounded : rounded);
}

export type UsOrgEconomics = {
  included: boolean;
  complete: boolean;
  openCount: number | null;
  notionalCents: number | null;
  unrealizedCents: number | null;
  pricedPositions: number;
  totalPositions: number;
  realizedGrossCents: number | null;
  realizedFeesCents: number | null;
  realizedNetCents: number | null;
};

/** Whole attributed ledger only; never sum the capped recent-fills display. */
export function usTraderRealizedCents(read: TraderPositions, field: 'grossUsd' | 'feesUsd' | 'netUsd'): number | null {
  if (!read.complete) return null;
  let total = 0n;
  for (const account of read.accountsById.values()) {
    if (!account.complete) return null;
    const value = account.realized?.[field];
    const exact = value == null ? null : signedDecimalUnits(value);
    if (exact === null) return null;
    total += exact;
  }
  return cents(total);
}

export function usOrgPositionEconomics(rows: readonly OrgUsPosition[], complete: boolean,
  markets: UsPositionMarks, nowMs: number) {
  let basis = 0n, quantity = 0n, pnl = 0;
  let basisKnown = complete, quantityKnown = complete, pricedPositions = 0;
  const seen = new Set<string>();
  for (const { row } of rows) {
    const identity = JSON.stringify([row.scope.scopeKey, row.target.marketSlug, row.target.outcome]);
    if (seen.has(identity)) { complete = false; basisKnown = false; quantityKnown = false; continue; }
    seen.add(identity);
    const economics = usPositionEconomics(row, markets.getMarket(row.target.marketSlug), nowMs);
    const q = row.heldQuantity === null ? null : signedDecimalUnits(row.heldQuantity);
    if (q === null || row.quantityBasis !== 'g1_ledger' || row.reasons.length) quantityKnown = false;
    else quantity += q;
    const b = economics.entryBasisDollars === null || row.accounting?.costBasisUsd == null
      ? null : signedDecimalUnits(row.accounting.costBasisUsd);
    if (b === null) basisKnown = false;
    else basis += b;
    if (economics.pnlDollars !== null) { pricedPositions++; pnl += economics.pnlDollars * 100; }
  }
  const openCount = Number(quantity) / 1e12;
  return {
    complete,
    openCount: quantityKnown && Number.isFinite(openCount) && openCount <= Number.MAX_SAFE_INTEGER ? openCount : null,
    notionalCents: basisKnown ? cents(basis) : null,
    unrealizedCents: complete && pricedPositions === rows.length ? pnl : null,
    pricedPositions, totalPositions: rows.length,
  };
}

const zero: UsOrgEconomics = { included: false, complete: true, openCount: 0, notionalCents: 0,
  unrealizedCents: 0, pricedPositions: 0, totalPositions: 0,
  realizedGrossCents: 0, realizedFeesCents: 0, realizedNetCents: 0 };

/** Member summaries are all-sports, as the existing K/P member headline is.
 * An absent US account is zero contribution; an unread/failed scope is unknown. */
export function usOrgMemberEconomics(current: UsOrgLoaded | null, userId: string,
  markets: UsPositionMarks, nowMs: number): UsOrgEconomics {
  if (current?.accounts === 'absent') return zero;
  const read = current?.positions?.members.get(userId);
  const complete = current?.positions?.scopeComplete === true && read?.complete === true;
  const rows = current?.positions?.rows.filter(p => p.owner.filterKey === `trader:${userId}`) ?? [];
  return { included: true, ...usOrgPositionEconomics(rows, complete, markets, nowMs),
    realizedGrossCents: complete && read ? usTraderRealizedCents(read, 'grossUsd') : null,
    realizedFeesCents: complete && read ? usTraderRealizedCents(read, 'feesUsd') : null,
    realizedNetCents: complete && read ? usTraderRealizedCents(read, 'netUsd') : null,
  };
}

/** A member's incomplete history must not suppress another member's verified
 * result. An incomplete outer scope still prevents either total being complete. */
export function usOrgOwnerComplete(current: UsOrgLoaded | null, owner: OwnerFilterKey): boolean {
  if (current?.accounts === 'absent') return true;
  if (!current?.positions?.scopeComplete) return false;
  if (owner.startsWith('trader:')) return current.positions.members.get(owner.slice('trader:'.length))?.complete === true;
  return current.positions.complete;
}

export const addKnown = (a: number | null | undefined, b: number | null): number | null =>
  a == null || b === null || !Number.isFinite(a + b) || Math.abs(a + b) > Number.MAX_SAFE_INTEGER ? null : a + b;
